// src/services/deviceService.js
import apiClient from '../api/client';

class DeviceService {
  constructor() {
    this.deviceId = null;
    this.deviceToken = null;
  }

  /**
   * Generate device fingerprint
   */
  async generateDeviceId() {
    // Simple fingerprint (you can use FingerprintJS for better accuracy)
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('fingerprint', 2, 2);
    
    const screenInfo = window.screen; // ⭐ Use window.screen instead
    
    const data = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screen: `${screenInfo.width}x${screenInfo.height}x${screenInfo.colorDepth}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      canvas: canvas.toDataURL()
    };
    
    // Simple hash
    const str = JSON.stringify(data);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    return `device_${Math.abs(hash).toString(36)}`;
  }

  /**
   * Get device info for backend
   */
  getDeviceInfo() {
    const screenInfo = window.screen; // ⭐ Use window.screen
    
    return {
      browser: navigator.userAgent,
      platform: navigator.platform,
      screen: `${screenInfo.width}x${screenInfo.height}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      online: navigator.onLine
    };
  }

  /**
   * Get device name (user-friendly)
   */
  getDeviceName() {
    const ua = navigator.userAgent;
    
    if (/mobile/i.test(ua)) return 'Mobile Device';
    if (/tablet|ipad/i.test(ua)) return 'Tablet';
    if (/mac/i.test(ua)) return 'Mac Computer';
    if (/windows/i.test(ua)) return 'Windows Computer';
    if (/linux/i.test(ua)) return 'Linux Computer';
    
    return 'Desktop Computer';
  }

  /**
   * Get device type
   */
  getDeviceType() {
    const ua = navigator.userAgent;
    
    if (/mobile/i.test(ua)) return 'mobile';
    if (/tablet|ipad/i.test(ua)) return 'tablet';
    return 'desktop';
  }

  /**
   * Register device with backend
   */
  async registerDevice() {
    if (!navigator.onLine) {
      throw new Error('Must be online to register device');
    }

    try {
      // Generate or get existing device ID
      this.deviceId = localStorage.getItem('device_id');
      
      if (!this.deviceId) {
        this.deviceId = await this.generateDeviceId();
        localStorage.setItem('device_id', this.deviceId);
      }

      // Register with backend
      const response = await apiClient.post('/devices/register/', {
        device_id: this.deviceId,
        device_name: this.getDeviceName(),
        device_type: this.getDeviceType(),
        device_info: this.getDeviceInfo()
      });

      // Store device token
      this.deviceToken = response.data.device_token;
      localStorage.setItem('device_token', response.data.device_token);
      localStorage.setItem('device_registered_at', new Date().toISOString());
      localStorage.setItem('offline_grace_days', response.data.offline_grace_days);
      localStorage.setItem('last_online_checkin', new Date().toISOString());


      return {
        success: true,
        ...response.data
      };

    } catch (error) {
      
      if (error.response?.status === 403) {
        // Device limit reached
        return {
          success: false,
          error: 'device_limit_reached',
          message: error.response.data.error,
          devices_used: error.response.data.devices_used,
          devices_limit: error.response.data.devices_limit
        };
      }

      throw error;
    }
  }

  /**
   * Check if device is registered
   */
  isRegistered() {
    return !!localStorage.getItem('device_token');
  }

  /**
   * Check if device needs online check-in
   */
  needsCheckin() {
    const lastCheckin = localStorage.getItem('last_online_checkin');
    const graceDays = parseInt(localStorage.getItem('offline_grace_days') || '7');

    if (!lastCheckin) return true;

    const daysSinceCheckin = Math.floor(
      (Date.now() - new Date(lastCheckin).getTime()) / (1000 * 60 * 60 * 24)
    );

    return daysSinceCheckin > graceDays;
  }

  /**
   * Get days until check-in required
   */
  getDaysUntilCheckin() {
    const lastCheckin = localStorage.getItem('last_online_checkin');
    const graceDays = parseInt(localStorage.getItem('offline_grace_days') || '7');

    if (!lastCheckin) return 0;

    const daysSinceCheckin = Math.floor(
      (Date.now() - new Date(lastCheckin).getTime()) / (1000 * 60 * 60 * 24)
    );

    return Math.max(0, graceDays - daysSinceCheckin);
  }

  /**
   * Validate device with backend
   */
  async validateDevice() {
    const deviceToken = localStorage.getItem('device_token');

    if (!deviceToken) {
      return { valid: false, error: 'not_registered' };
    }

    // If offline and within grace period, allow
    if (!navigator.onLine) {
      if (!this.needsCheckin()) {
        return { valid: true, offline: true };
      } else {
        return {
          valid: false,
          error: 'checkin_required',
          message: 'Please connect to internet to continue'
        };
      }
    }

    // Online: validate with backend
    try {
      const response = await apiClient.post('/devices/validate/', {
        device_token: deviceToken
      });

      if (response.data.valid) {
        // Update last check-in
        localStorage.setItem('last_online_checkin', new Date().toISOString());
      }

      return response.data;

    } catch (error) {
      return {
        valid: false,
        error: 'validation_failed',
        message: error.response?.data?.error || 'Device validation failed'
      };
    }
  }
}

export default new DeviceService();
